import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { ImpersonationSession } from '@/types';

// Persist in sessionStorage so the banner survives SPA navigation — the
// super admin must be able to leave /admin/* to act as the impersonated user
// and still see the banner + End-Session control. Prior implementation lived
// inside SuperAdminLayout and unmounted on navigation away.
const STORAGE_KEY = 'secure-gate:impersonation-session';

// BroadcastChannel keeps other tabs in sync. Without this, Tab B would
// have the impersonation cookie but no banner and no refresh-block,
// because sessionStorage is tab-scoped while cookies are browser-wide.
const CHANNEL_NAME = 'secure-gate:impersonation-sync';

interface ImpersonationContextValue {
  session: ImpersonationSession | null;
  setSession: (session: ImpersonationSession | null) => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  session: null,
  setSession: () => undefined,
});

function readPersisted(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationSession) : null;
  } catch {
    return null;
  }
}

function writePersisted(next: ImpersonationSession | null): void {
  try {
    if (next) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore storage failures (private browsing, quota)
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<ImpersonationSession | null>(readPersisted);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Open a BroadcastChannel once and clean up on unmount.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const bc = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = bc;

    bc.onmessage = (event: MessageEvent<ImpersonationSession | null>) => {
      const incoming = event.data;
      setSessionState(incoming);
      writePersisted(incoming);
    };

    return () => {
      bc.close();
      channelRef.current = null;
    };
  }, []);

  const setSession = useCallback((next: ImpersonationSession | null) => {
    setSessionState(next);
    writePersisted(next);

    // Broadcast to other tabs so they show/hide the banner and
    // block/unblock token refresh in sync with this tab.
    try {
      channelRef.current?.postMessage(next);
    } catch {
      // Channel may be closed if unmount races with a mutation callback.
    }
  }, []);

  const value = useMemo(() => ({ session, setSession }), [session, setSession]);

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useImpersonation() {
  return useContext(ImpersonationContext);
}
